import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

// AI Assistant review — proxied through the dashboard server to the
// nano-fibre-assistant server, gated on Nano-Support-Access (full admins
// bypass). `days` null = all time.
//
// A review page must show the question asked a minute ago, whatever the
// app-wide query defaults are — so always refetch.
const useReviewQuery = (options) =>
  useQuery({ staleTime: 0, refetchOnMount: 'always', refetchOnWindowFocus: true, ...options });

const get = async (path, params) => (await api.get(`/api/assistant-review${path}`, { params })).data;

export function useAssistantSummary(days) {
  return useReviewQuery({ queryKey: ['assistantReview', 'summary', days], queryFn: () => get('/summary', { days }), retry: false });
}

// escalatedOnly: just the questions that got the safety escalation message.
export function useAssistantCommon(days, escalatedOnly = false) {
  return useReviewQuery({
    queryKey: ['assistantReview', 'common', days, escalatedOnly],
    queryFn: () => get('/common', { days, limit: 100, ...(escalatedOnly ? { escalated: 1 } : {}) }),
  });
}

export function useAssistantUnanswered(days) {
  return useReviewQuery({ queryKey: ['assistantReview', 'unanswered', days], queryFn: () => get('/unanswered', { days, limit: 100 }) });
}

export function useAssistantWeak(days) {
  return useReviewQuery({ queryKey: ['assistantReview', 'weak', days], queryFn: () => get('/weak', { days, limit: 100 }) });
}

export function useAssistantFlagged(status) {
  return useReviewQuery({ queryKey: ['assistantReview', 'flagged', status], queryFn: () => get('/flagged', { status, limit: 100 }) });
}

export function useAssistantDocumentChanges() {
  return useReviewQuery({ queryKey: ['assistantReview', 'documentChanges'], queryFn: () => get('/document-changes', { days: 90 }) });
}

export function useAssistantGroup(id) {
  return useReviewQuery({
    queryKey: ['assistantReview', 'group', id],
    queryFn: () => get(`/groups/${id}`, { limit: 100 }),
    enabled: Boolean(id),
  });
}

function useReviewMutation(pathFor) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, note }) => api.post(`/api/assistant-review${pathFor(id)}`, { note }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assistantReview'] }),
  });
}

export function useReviewGroup() {
  return useReviewMutation((id) => `/groups/${id}/review`);
}

export function useReviewFlag() {
  return useReviewMutation((id) => `/flags/${id}/review`);
}

// Every question asked, newest first, whatever answered it — including
// retrieval mode, which the API vs pool list leaves out.
// mode: 'all' | 'api' | 'pool' | 'retrieval'.
export function useAssistantAllQuestions(days, mode) {
  return useReviewQuery({
    queryKey: ['assistantReview', 'questions', days, mode],
    queryFn: () => get('/questions', { days, mode, limit: 200 }),
  });
}

// source: 'all' | 'api' | 'pool' — where each answer came from; pool answers
// carry the original API question/answer they reused.
export function useAssistantSources(days, source) {
  return useReviewQuery({
    queryKey: ['assistantReview', 'sources', days, source],
    queryFn: () => get('/sources', { days, source, limit: 100 }),
  });
}

// A reviewer takes a saved answer out of the pool; the next asker gets a fresh
// API answer. id = answer_pool id.
export function useStopReusing() {
  return useReviewMutation((id) => `/pool/${id}/retire`);
}

// Word pairs the answer pool learned by itself ("lino" = "rug"), newest first,
// forgotten ones last.
export function useLearnedWords() {
  return useReviewQuery({ queryKey: ['assistantReview', 'learnedWords'], queryFn: () => get('/learned-words') });
}

// Questions the API answered that came close to a saved one — a reviewer's
// "Same question" teaches the pool that wording; "Different" keeps them apart.
export function useNearMisses(days) {
  return useReviewQuery({ queryKey: ['assistantReview', 'nearMisses', days], queryFn: () => get('/near-misses', { days }) });
}

export function useNearMissDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ questionLogId, poolId, decision }) =>
      api.post(`/api/assistant-review/near-misses/${questionLogId}/${decision}`, { poolId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assistantReview'] }),
  });
}
